import axios from "axios";
import * as Sentry from "@sentry/react";

import { REQUEST_MAX_SIZE, REQUEST_MAX_TIME, URL_API_BASE } from "../constants";
import { globalReauthorizeActions } from "./store";

/**
 * Create and configure an axios instance for communicating with the API
 */
const api = axios.create({
  baseURL: URL_API_BASE,
  timeout: REQUEST_MAX_TIME,
  maxContentLength: REQUEST_MAX_SIZE,
  headers: {
    "Cache-Control": "no-cache,no-store,must-revalidate,max-age=-1,private",
    Pragma: "no-cache",
    Expires: "Sat, 01 Jan 2000 00:00:00 GMT",
  },
});

/**
 * Remove authorization header from API requests
 * @extends api
 */
export const apiDeauthorize = () => {
  if (api.defaults.headers.common.hasOwnProperty("Authorization")) {
    delete api.defaults.headers.common.Authorization;
  }
};

/**
 * Add authorization header to API requests
 * @param {string} authToken
 */
export const apiAuthorize = authToken => {
  if (!!authToken) {
    api.defaults.headers.common.Authorization = `Bearer ${authToken}`;
  } else {
    apiDeauthorize();
  }
};

/**
 * Get constructor needed to cancel API requests
 */
export const CancelToken = axios.CancelToken;

/**
 * Parse error message from API response
 * @param {error|object|string} error
 * @public
 */
const parseApiErrorMessage = (error, status) => {
  if (!!error) {
    try {
      if (typeof error === "string" || error instanceof String) {
        return error;
      }

      const errorData = error.data || (!!error.response && error.response.data);

      if (
        !!errorData &&
        (typeof errorData === "string" || errorData instanceof String)
      ) {
        return errorData;
      }

      if (!!errorData && (!!errorData.message || !!errorData.error)) {
        let message = errorData.message || errorData.error;

        if (!!errorData.errors) {
          const errors = Object.keys(error.response.data.errors).map(k =>
            error.response.data.errors[k].join(" ")
          );
          message += ` ${errors.join(", ")}`;
        }

        if (message === "Undefined index: browser-error") {
          message = "Could not connect to reCAPTCHA. Please try again.";
        }

        return message;
      }

      if (!!status) {
        switch (status) {
          case 400:
            return "Bad Request";
          case 401:
            return "Unauthorized Request";
          case 403:
            return "Forbidden Request";
          case 404:
            return "Request Not Found";
          case 429:
            return "Too Many Requests";
          case 500:
            return "Server Error";
          case 503:
            return "Service Unavailable";
          default:
            return `${status} Error`;
        }
      }

      if (!!error.message) {
        return error.message;
      }
    } catch (e) {}
  }

  return "Unknown error";
};

/**
 * Get error message from API response
 * @param {error|object|string} error
 * @param {bool} isAuth
 * @public
 */
export const getApiErrorMessage = (error, isAuth) => {
  const status = (!!error && !!error.response && error.response.status) || null;
  const errorMessage = parseApiErrorMessage(error, status);

  if (process.env.NODE_ENV === "development") {
    console.trace(errorMessage, error);
  } else if (!status || (!!status && status >= 500)) {
    try {
      Sentry.captureException(error, {
        errorMessage: errorMessage,
        errorObject: error,
      });
    } catch (e) {}
  }

  if (status === 401 && !isAuth) {
    globalReauthorizeActions.toggle();
    return;
  }

  return errorMessage;
};

export default api;
